import React from 'react';

function clampColumns(value, fallback = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.min(4, Math.floor(parsed)));
}

function classNames(...values) {
  return values.filter(Boolean).join(' ');
}

function LinkWrapper({ href, children }) {
  if (!href) return children;

  const isExternal = /^https?:\/\//i.test(href);
  return (
    <a href={href} className="article-markdoc-card__link" target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noreferrer' : undefined}>
      {children}
    </a>
  );
}

export function MarkdocTable({ columns = 2, children }) {
  const normalizedColumns = clampColumns(columns);
  return (
    <section className="article-markdoc-table" style={{ '--md-columns': normalizedColumns }}>
      {children}
    </section>
  );
}

export function MarkdocRow({ columns = 2, reverse = false, children }) {
  const normalizedColumns = clampColumns(columns);
  return (
    <div
      className={classNames('article-markdoc-row', reverse ? 'is-reversed' : '')}
      style={{ '--md-columns': normalizedColumns }}
    >
      {children}
    </div>
  );
}

export function MarkdocCol({ span = 1, children }) {
  const normalizedSpan = Math.max(1, Math.min(4, Math.floor(Number(span) || 1)));
  return (
    <div className="article-markdoc-col" style={{ '--md-span': normalizedSpan }}>
      {children}
    </div>
  );
}

export function MarkdocCallout({ tone = 'info', title, children }) {
  const normalizedTone = ['info', 'success', 'warning', 'danger'].includes(String(tone)) ? tone : 'info';
  return (
    <aside className={classNames('article-markdoc-callout', `tone-${normalizedTone}`)}>
      {title ? <h4>{title}</h4> : null}
      <div>{children}</div>
    </aside>
  );
}

export function MarkdocCards({ columns = 3, children }) {
  const normalizedColumns = clampColumns(columns, 3);
  return (
    <section className="article-markdoc-cards" style={{ '--md-columns': normalizedColumns }}>
      {children}
    </section>
  );
}

export function MarkdocCard({ title, href, children }) {
  const content = (
    <article className="article-markdoc-card">
      {title ? <h4>{title}</h4> : null}
      <div>{children}</div>
    </article>
  );

  return <LinkWrapper href={href}>{content}</LinkWrapper>;
}

export function MarkdocGallery({ columns = 3, children }) {
  const normalizedColumns = clampColumns(columns, 3);
  return (
    <section className="article-markdoc-gallery" style={{ '--md-columns': normalizedColumns }}>
      {children}
    </section>
  );
}

export function MarkdocStats({ columns = 3, children }) {
  const normalizedColumns = clampColumns(columns, 3);
  return (
    <section className="article-markdoc-stats" style={{ '--md-columns': normalizedColumns }}>
      {children}
    </section>
  );
}

export function MarkdocStat({ value, label, children }) {
  return (
    <article className="article-markdoc-stat">
      {value ? <h4 className="article-markdoc-stat__value">{value}</h4> : null}
      {label ? <p className="article-markdoc-stat__label">{label}</p> : null}
      <div>{children}</div>
    </article>
  );
}

export function MarkdocTimeline({ children }) {
  return <section className="article-markdoc-timeline">{children}</section>;
}

export function MarkdocTimelineItem({ title, meta, children }) {
  return (
    <article className="article-markdoc-timeline-item">
      <div className="article-markdoc-timeline-item__dot" aria-hidden="true" />
      <div className="article-markdoc-timeline-item__content">
        {(title || meta) ? (
          <header className="article-markdoc-timeline-item__header">
            {title ? <h4>{title}</h4> : null}
            {meta ? <span>{meta}</span> : null}
          </header>
        ) : null}
        <div>{children}</div>
      </div>
    </article>
  );
}

export function MarkdocFaq({ children }) {
  return <section className="article-markdoc-faq">{children}</section>;
}

export function MarkdocFaqItem({ question, children, open = false }) {
  return (
    <details className="article-markdoc-faq-item" open={Boolean(open)}>
      <summary>{question || 'Question'}</summary>
      <div>{children}</div>
    </details>
  );
}

export function MarkdocQuote({ author, role, children }) {
  return (
    <figure className="article-markdoc-quote">
      <blockquote>{children}</blockquote>
      {(author || role) ? (
        <figcaption>
          {author ? <strong>{author}</strong> : null}
          {author && role ? ' - ' : null}
          {role ? <span>{role}</span> : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

export function MarkdocHeroSplit({
  eyebrow,
  title,
  subtitle,
  image,
  imageAlt,
  reverse = false,
  ctaText,
  ctaHref,
  children,
}) {
  const body = (
    <section className={classNames('article-markdoc-hero-split', reverse ? 'is-reversed' : '')}>
      <div className="article-markdoc-hero-split__content">
        {eyebrow ? <p className="article-markdoc-hero-split__eyebrow">{eyebrow}</p> : null}
        {title ? <h3>{title}</h3> : null}
        {subtitle ? <p className="article-markdoc-hero-split__subtitle">{subtitle}</p> : null}
        <div>{children}</div>
        {ctaText ? <span className="article-markdoc-hero-split__cta">{ctaText}</span> : null}
      </div>
      {image ? (
        <figure className="article-markdoc-hero-split__media">
          <img src={image} alt={imageAlt || title || 'Section visual'} />
        </figure>
      ) : null}
    </section>
  );

  return ctaText && ctaHref ? <LinkWrapper href={ctaHref}>{body}</LinkWrapper> : body;
}

export function MarkdocFeatureGrid({ columns = 3, children }) {
  const normalizedColumns = clampColumns(columns, 3);
  return (
    <section className="article-markdoc-feature-grid" style={{ '--md-columns': normalizedColumns }}>
      {children}
    </section>
  );
}

export function MarkdocFeatureItem({ title, icon, children }) {
  return (
    <article className="article-markdoc-feature-item">
      {icon ? <span className="article-markdoc-feature-item__icon">{icon}</span> : null}
      {title ? <h4>{title}</h4> : null}
      <div>{children}</div>
    </article>
  );
}

export function MarkdocProcess({ title, children }) {
  return (
    <section className="article-markdoc-process">
      {title ? <h3>{title}</h3> : null}
      <div>{children}</div>
    </section>
  );
}

export function MarkdocStep({ title, number, children }) {
  return (
    <article className="article-markdoc-step">
      <div className="article-markdoc-step__badge">{number || 'Step'}</div>
      <div>
        {title ? <h4>{title}</h4> : null}
        <div>{children}</div>
      </div>
    </article>
  );
}

export function MarkdocCtaBand({ title, buttonText, buttonHref, tone = 'primary', children }) {
  const normalizedTone = ['primary', 'secondary'].includes(String(tone)) ? tone : 'primary';
  const button = buttonText ? <span className="article-markdoc-cta-band__button">{buttonText}</span> : null;

  return (
    <section className={classNames('article-markdoc-cta-band', `tone-${normalizedTone}`)}>
      {title ? <h3>{title}</h3> : null}
      <div>{children}</div>
      {buttonHref && button ? <LinkWrapper href={buttonHref}>{button}</LinkWrapper> : button}
    </section>
  );
}